import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Tag, Plus, Pencil, Wallet, Trash, SignOut, CaretUp, CaretDown } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../api';

const Categories = ({ onLogout }) => {
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [keywords, setKeywords] = useState('');
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Settings
  const [highValueThreshold, setHighValueThreshold] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  
  // New category
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryKeywords, setNewCategoryKeywords] = useState('');
  
  // Edit dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Delete state
  const [deletingId, setDeletingId] = useState(null);

  const handleLogout = () => {
    localStorage.removeItem('trackcc_token');
    localStorage.removeItem('trackcc_biometric');
    toast.success('Logged out successfully');
    if (onLogout) onLogout();
  };

  useEffect(() => {
    fetchCategories();
    fetchSettings();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setHighValueThreshold(response.data.high_value_threshold.toString());
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/settings', {
        high_value_threshold: parseFloat(highValueThreshold),
      });
      toast.success('High value threshold updated');
    } catch (error) {
      toast.error('Failed to update threshold');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleUpdateKeywords = async () => {
    if (!editingCategory) return;

    const keywordArray = keywords
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    try {
      await api.put(`/categories/${editingCategory.id}`, {
        keywords: keywordArray,
      });
      toast.success('Keywords updated');
      setShowEditDialog(false);
      setEditingCategory(null);
      setKeywords('');
      setNewName('');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleRename = async () => {
    if (!editingCategory || !newName.trim()) return;

    try {
      await api.put(`/categories/${editingCategory.id}/rename`, {
        name: newName.trim(),
      });
      toast.success(`Category renamed to ${newName.toUpperCase()}`);
      setShowEditDialog(false);
      setEditingCategory(null);
      setKeywords('');
      setNewName('');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to rename category');
    }
  };

  const handleDelete = async (categoryId, categoryName) => {
    setDeletingId(categoryId);
    try {
      const response = await api.delete(`/categories/${categoryId}`);
      toast.success(`Deleted "${categoryName}". ${response.data.recategorized_count} transactions recategorized.`);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Category name required');
      return;
    }

    const keywordArray = newCategoryKeywords
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    try {
      await api.post('/categories', {
        name: newCategoryName.toUpperCase(),
        keywords: keywordArray,
      });
      toast.success('Category added');
      setShowAddDialog(false);
      setNewCategoryName('');
      setNewCategoryKeywords('');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to add category');
    }
  };

  const openEdit = (category) => {
    setEditingCategory(category);
    setKeywords(category.keywords.join(', '));
    setNewName(category.name);
    setShowEditDialog(true);
  };

  const handleMoveCategory = async (index, direction) => {
    // Don't allow moving ETC
    if (categories[index].name === 'ETC') return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Don't move above first or below ETC
    if (newIndex < 0) return;
    if (categories[newIndex].name === 'ETC') return;
    
    // Swap the categories
    const newCategories = [...categories];
    [newCategories[index], newCategories[newIndex]] = [newCategories[newIndex], newCategories[index]];
    
    // Update sort orders
    const categoryOrders = newCategories
      .filter(cat => cat.name !== 'ETC')
      .map((cat, idx) => ({ id: cat.id, sort_order: idx }));
    
    try {
      await api.put('/categories/reorder', { category_orders: categoryOrders });
      setCategories(newCategories);
      toast.success('Category order updated');
    } catch (error) {
      toast.error('Failed to reorder categories');
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-muted rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-6 space-y-6"
      data-testid="categories-page"
    >
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
          Settings
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage categories & high value threshold
        </p>
      </div>

      {/* High Value Threshold */}
      <Card className="border-2 border-red-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Wallet size={20} weight="bold" />
            High Value Threshold
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label>Amount</Label>
            <Input
              type="number"
              value={highValueThreshold}
              onChange={(e) => setHighValueThreshold(e.target.value)}
              placeholder="9000"
              className="border-2 border-red-800 focus:border-red-800 focus:ring-red-800"
              data-testid="high-value-threshold-input"
            />
            <p className="text-xs text-muted-foreground">
              Transactions above this amount appear in High Value page
            </p>
          </div>
          <Button
            onClick={handleSaveSettings}
            disabled={savingSettings}
            className="w-full rounded-full bg-[#800000] hover:bg-[#600000] text-white"
            data-testid="save-threshold-button"
          >
            {savingSettings ? 'Saving...' : 'Update Threshold'}
          </Button>
        </CardContent>
      </Card>

      {/* Add Category Button */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogTrigger asChild>
          <Button className="w-full rounded-full" data-testid="add-category-button">
            <Plus size={20} weight="bold" className="mr-2" />
            Add New Category
          </Button>
        </DialogTrigger>
        <DialogContent data-testid="add-category-dialog">
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="e.g., TRANSPORT"
                data-testid="new-category-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Keywords (comma separated)</Label>
              <Input
                value={newCategoryKeywords}
                onChange={(e) => setNewCategoryKeywords(e.target.value)}
                placeholder="Uber, Ola, Metro"
                data-testid="new-category-keywords"
              />
              <p className="text-xs text-muted-foreground">
                Transactions containing these keywords will be categorized automatically
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleAddCategory}
              className="rounded-full w-full"
              data-testid="save-new-category-button"
            >
              Add Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent data-testid="edit-category-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editingCategory?.name}</DialogTitle>
            <DialogDescription>
              Update category name or keywords
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Category name"
                data-testid="rename-category-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Keywords (comma separated)</Label>
              <Input
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="Keyword1, Keyword2, Keyword3"
                data-testid="keywords-input"
              />
              <p className="text-xs text-muted-foreground">
                Transactions containing these keywords will be categorized automatically
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              onClick={handleRename}
              disabled={!newName.trim() || newName.toUpperCase() === editingCategory?.name}
              className="rounded-full w-full"
              variant="outline"
              data-testid="rename-category-button"
            >
              Rename to {newName.toUpperCase()}
            </Button>
            <Button
              onClick={handleUpdateKeywords}
              className="rounded-full w-full"
              data-testid="save-category-button"
            >
              Save Keywords
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Categories List */}
      <div className="space-y-4">
        {categories.map((category, index) => (
          <Card key={category.id} data-testid="category-card">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag size={20} weight="bold" className="text-primary" />
                    <h3 className="font-bold text-lg">{category.name}</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {category.keywords.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No keywords (catches all remaining)</p>
                    ) : (
                      category.keywords.map((keyword, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {keyword}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {/* Sort arrows - not for ETC */}
                  {category.name !== 'ETC' && (
                    <div className="flex flex-col">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1"
                        onClick={() => handleMoveCategory(index, 'up')}
                        disabled={index === 0}
                        data-testid="move-up-button"
                        title="Move up"
                      >
                        <CaretUp size={16} weight="bold" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1"
                        onClick={() => handleMoveCategory(index, 'down')}
                        disabled={index >= categories.length - 2 || categories[index + 1]?.name === 'ETC'}
                        data-testid="move-down-button"
                        title="Move down"
                      >
                        <CaretDown size={16} weight="bold" />
                      </Button>
                    </div>
                  )}
                  {category.name !== 'ETC' && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(category)}
                        data-testid="edit-category-button"
                      >
                        <Pencil size={20} />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid="delete-category-button"
                          >
                            <Trash size={20} className="text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {category.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will delete the category and recategorize all its transactions using keyword matching. Transactions that don't match any other category will move to ETC.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(category.id, category.name)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              disabled={deletingId === category.id}
                            >
                              {deletingId === category.id ? 'Deleting...' : 'Delete'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Logout Button */}
      <Card className="mt-4">
        <CardContent className="p-4">
          <Button 
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            onClick={handleLogout}
          >
            <SignOut size={18} className="mr-2" />
            Logout
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default Categories;
